import Api from 'fou-api'
import VuexErrors from 'src/vuex/modules/errors'
import Errors from 'src/utils/helper/errors'
import { cloneDeep, each, isEmpty, isEqual, uniqBy, omit } from 'lodash'

export const initialPagination = {
  page: 1,
  nextPage: 10,
  perPage: 30
}

export default {
  namespaced: true,
  state() {
    return {
      bundleStatus: {},
      bundleInfo: {
        name: '',
        foundries: [],
        description: '',
        partNumber: '',
        collection: [],
        assets: {
          main: [],
          inspirational: []
        },
        prices: [],
      },
      families: [],
      pagination: initialPagination,
      familyDetails: [],
      isFamiliesLoading: false,
      foundriesData: [],
      isFoundryRemoveModalOpen: false,
      isPreviewDraftModalOpen: false,
      addFontsValidationErrors: null,
      isConfirmPublishModalOpen: false,
      isBundlePublishing: false,
      shouldNavigateToBundleSuccess: false,
      isConfirmDiscardModalOpen: false,
      isBundleDiscarding: false,
      bundleState: 'published',
      totalImagesToUpload: 0,
      loading: false,
      paginationCurrentPage: 1,
      bundleId: '',
      deReleaseDate: '',
      alreadyUploadedMd5: [],
      bundleMainImageScanStatus: "unset",
      bundleInspirationImageScanStatus: "unset",
      bundleMainImagesWithFalseAvCheck: [],
      bundleInspirationImagesWithFalseAvCheck: []
    }
  },
  getters: {
    getLoading: state => {
      return state.loading
    },
    allImageUpload: (state) => {
      return state.totalImagesToUpload
    },
    getBundleInfo: state => {
      return state.bundleInfo
    },
    getBundleStatus: state => {
      return state.bundleStatus
    },
    existedSkuIds: state => {
      let skuIds = []
      if (isEmpty(state.bundleInfo?.collection)) return skuIds

      skuIds = state.bundleInfo.collection.map(sku => sku.skuId)

      return skuIds
    },
    existedFontStyleIds: state => {
      let fontStyleIds = []
      if (isEmpty(state.bundleInfo?.collection)) return fontStyleIds

      fontStyleIds = state.bundleInfo.collection.map(family => family.styleId)

      return fontStyleIds
    },
    /* in order to reformat family properties formattedFamilies is used */
    formattedFamilies: (state, getters) => {
      return state.families.map(family => {
        each(family.skus, sku => {
          each(sku.styles, style => {
            style.isFontStyleAdded = getters.existedFontStyleIds.includes(style.styleId)
          })
        })

        const existedFoundry = state.foundriesData.find(foundry => isEqual(foundry.externalId, family.vendorID))

        if (!isEmpty(existedFoundry)) {
          family.foundryId = existedFoundry.id
        }

        family.familyId = family.id
        family.familyName = family.name
        return family
      })
    },
    newUpdatedMainSortOrder: (state) => {
      return state.bundleInfo.assets.main
    },
    newUpdatedInspirationSortOrder: (state) => {
      return state.bundleInfo.assets.inspirational
    },
    getPaginationCurrentPage: state => {
      return state.paginationCurrentPage;
    },
    getBundleId: state => {
      return state.bundleId
    },
    getDeReleaseDate: state => {
      return state.deReleaseDate
    },
    getBundleLive: state => {
      return state.bundleLive
    },
    getAlreadyUploadedMd5: state => {
      return state.alreadyUploadedMd5
    },
    getBundleMainImageScanStatus: state => {
      return state.bundleMainImageScanStatus
    },
    getBundleInspirationImageScanStatus: state => {
      return state.bundleInspirationImageScanStatus
    },
    getBundleMainImagesWithFalseAvCheck: state => {
      return state.bundleMainImagesWithFalseAvCheck
    },
    getBundleInspirationImagesWithFalseAvCheck: state => {
      return state.bundleInspirationImagesWithFalseAvCheck
    }
  },
  mutations: {
    updateLoader(state, payload) {
      state.loading = payload
    },
    incrementTotalImagesToUpload(state) {
      state.totalImagesToUpload++;
    },
    decrementTotalImagesToUpload(state) {
      state.totalImagesToUpload--;
    },
    UPDATE_BUNDLE_INFO(state, bundleInfo) {
      state.bundleInfo = bundleInfo
    },
    UPDATE_BUNDLE_STATE(state, bundleState) {
      state.bundleState = bundleState
    },
    UPDATE_BUNDLE_NAME(state, bundleName) {
      state.bundleInfo.name = bundleName
    },
    UPDATE_DESCRIPTION(state, description) {
      state.bundleInfo.description = description
    },
    UPDATE_FOUNDRIES(state, foundries) {
      state.bundleInfo.foundries = foundries
    },
    UPDATE_BUNDLE_STATUS(state, bundleInfo) {
      state.bundleStatus = bundleInfo
      const {
        name,
        foundries,
        description,
        partNumber,
        collection = [],
        assets = { main: [], inspirational: [] },
        prices = []
      } = bundleInfo
      state.bundleInfo = { name, foundries, description, partNumber, collection, assets, prices }
    },
    UPDATE_PART_NUMBER(state, partNumber) {
      state.bundleInfo.partNumber = partNumber
    },
    RESET_BUNDLE(state) {
      state.bundleStatus = {}
      state.alreadyUploadedMd5 = []
      state.bundleMainImageScanStatus = "unset"
      state.bundleInspirationImageScanStatus = "unset"
      state.bundleMainImagesWithFalseAvCheck = []
      state.bundleInspirationImagesWithFalseAvCheck = []
      state.bundleInfo = {
        name: '',
        foundries: [],
        description: '',
        partNumber: '',
        collection: [],
        assets: {
          main: [],
          inspirational: []
        }
      }
    },
    UPDATE_FONT_LIST(state, payload) {
      state.bundleInfo.collection = state.bundleInfo.collection.filter(sku => !isEqual(sku.styleId, payload.styleId))
    },
    UPDATE_BUNDLE_IMAGE(state, file) {
      let imagesMain = state.bundleInfo.assets.main
      state.bundleInfo.assets.main = [...imagesMain, ...file]
    },
    REMOVE_ALL_BUNDLE_IMAGES(state) {
      state.bundleInfo.assets.main = [];
    },
    REMOVE_ALL_INSPIRATION_BUNDLE_IMAGES(state) {
      state.bundleInfo.assets.inspirational = [];
    },
    UPDATE_INSPIRATIONAL_IMAGE(state, file) {
      let imagesInsiprational = state.bundleInfo.assets.inspirational
      state.bundleInfo.assets.inspirational = [...imagesInsiprational, ...file]
    },
    REMOVE_BUNDLE_IMAGE(state, imageId) {
      state.bundleInfo.assets.main = state.bundleInfo.assets.main.filter(image => !isEqual(image.id, imageId))
    },
    REMOVE_INSPIRATIONAL_IMAGE(state, imageId) {
      state.bundleInfo.assets.inspirational = state.bundleInfo.assets.inspirational.filter(image => !isEqual(image.id, imageId))
    },
    UPDATE_BUNDLE_PRICES(state, newPrices) {
      state.bundleInfo.prices = newPrices.map(newPrice => {
        newPrice.price = parseFloat(newPrice.price)
        return newPrice
      })
    },
    updateNewOrderedBundleImages(state, imageList) {
      state.bundleInfo.assets.main = [...imageList]
    },
    updateNewOrderedInspirationalImages(state, imageList) {
      state.bundleInfo.assets.inspirational = [...imageList]
    },
    setFamilies(state, families) {
      state.families = families
    },
    setPagination(state, pagination) {
      state.pagination = pagination
    },
    setFamilyDetails(state, familyDetails) {
      state.familyDetails = familyDetails
    },
    setFamiliesLoading(state, isFamiliesLoading) {
      state.isFamiliesLoading = isFamiliesLoading
    },
    setFoundriesData(state, options) {
      state.foundriesData = options
    },
    setIsFoundryRemoveModalOpen(state, status) {
      state.isFoundryRemoveModalOpen = status
    },
    setIsPreviewDraftModalOpen(state, status) {
      state.isPreviewDraftModalOpen = status
    },
    setAddFontsValidationErrors(state, errors) {
      state.addFontsValidationErrors = errors
    },
    setConfirmPublishModalOpen(state, status) {
      state.isConfirmPublishModalOpen = status
    },
    setBundlePublishing(state, status) {
      state.isBundlePublishing = status
    },
    setShouldNavigateToBundleSuccess(state, status) {
      state.shouldNavigateToBundleSuccess = status
    },
    setConfirmDiscardModalOpen(state, status) {
      state.isConfirmDiscardModalOpen = status
    },
    setBundleDiscarding(state, status) {
      state.isBundleDiscarding = status
    },
    setPaginationCurrentPage(state, value) {
      state.paginationCurrentPage = value
    },
    setBundleId(state, bundleId) {
      state.bundleId = bundleId
    },
    setDeReleaseDate(state, deReleaseDate) {
      state.deReleaseDate = deReleaseDate
    },
    setBundleLive(state, bundle) {
      state.bundleLive = bundle
    },
    setAlreadyUploadedMd5: (state, md5Bundle = []) => {
      state.alreadyUploadedMd5 = [
        ...new Map(
          [...state.alreadyUploadedMd5, ...md5Bundle].map(item => [`${item.md5}-${item.imageType}`, item])
        ).values(),
      ];
    },
    updateAlreadyUploadedMd5: (state, md5) => {
      state.alreadyUploadedMd5 = state.alreadyUploadedMd5.filter(item => item.md5 !== md5);
    },
    setBundleMainImageScanStatus: (state, value) => {
      state.bundleMainImageScanStatus = value
    },
    setBundleInspirationImageScanStatus: (state, value) => {
      state.bundleInspirationImageScanStatus = value
    },
    setBundleMainImagesWithFalseAvCheck: (state, value) => {
      state.bundleMainImagesWithFalseAvCheck = value
    },
    setBundleInpirationImagesWithFalseAvCheck: (state, value) => {
      state.bundleInspirationImagesWithFalseAvCheck = value
    }
  },
  actions: {
    createBundle({ commit }, payload) {
      return new Promise((resolve, reject) => {
        Api.Bundles.createBundle(payload)
          .then(response => {
            commit('UPDATE_BUNDLE_STATUS', response.data.bundle)
            resolve(response.data)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },
    checkisBundleUnique({ commit }, payload) {
      return new Promise((resolve, reject) => {
        Api.Bundles.checkisBundleUnique(payload.bundleName, payload.bundleId)
          .then(response => {
            return resolve(response.data.isBundleUnique)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },
    updateBundle({ commit, state }, bundleId) {
      return new Promise((resolve, reject) => {
        if (!isEmpty(state.bundleInfo.collection)) {
          state.bundleInfo.collection = state.bundleInfo.collection.map((style, index) => {
            return { ...style, rank: index + 1 }
          })
        }
        /**
       * add MSRP to bundle payload if not present
       */

        if (!isEmpty(state.bundleInfo.prices)) {
          const msrp = state.bundleInfo.prices.find(curr => isEqual(curr.currency, 'USD'))
          state.bundleInfo.msrp = msrp.price
        }
        if (!isEmpty(state.bundleInfo.assets.main)) {
          state.bundleInfo.assets.main.forEach((image, index) => {
            image.sortOrder = index + 1
          })
        }
        if (!isEmpty(state.bundleInfo.assets.inspirational)) {
          state.bundleInfo.assets.inspirational.forEach((image, index) => {
            image.sortOrder = index + 1
          })
        }
        Api.Bundles.updateBundle(bundleId, state.bundleInfo)
          .then(response => {
            commit('UPDATE_BUNDLE_STATUS', response.data.bundle)
            resolve(response.data)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    getBundleInfo({ commit }, bundleId) {
      return new Promise((resolve, reject) => {
        Api.Bundles.getBundle(bundleId)
          .then(response => {
            commit('UPDATE_BUNDLE_STATUS', response.data.bundle)
            resolve(response.data.bundle)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    deleteDraft({ commit }, bundleId) {
      commit('updateLoader', true)
      return new Promise((resolve, reject) => {
        Api.Bundles.delete(bundleId)
          .then(response => {
            commit('updateLoader', false)
            resolve(response)
          })
          .catch(error => {
            commit('updateLoader', false)
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    getBundleList({ commit }, payload) {
      return new Promise((resolve, reject) => {
        // Most of the vue condition checks for the published. So, did not change those
        // Only the api data is altered
        const PUBLISHED = 'published'
        const LIVE = 'live'
        const bundleType = isEqual(payload.type, PUBLISHED) ? LIVE : payload.type
        Api.Bundles.getBundles(
          bundleType,
          payload.foundries,
          payload.search,
          payload.page,
          payload.perPage,
          payload.sortBy,
          payload.sortDirection,
          payload.mode,
        )
          .then(response => {
            resolve(response.data)
            if (isEqual(bundleType, LIVE)) {
              commit('setBundleLive', response.data)
            }
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    fetchFamiliesByFoundries({ commit }, payload) {
      return new Promise((resolve, reject) => {
        Api.Families.getFamiliesByFoundries(
          payload?.foundryIds,
          payload?.page,
          payload?.perPage,
          payload?.search,
          payload?.includeCount
        )
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            let err = Errors.create(error)
            commit('errors/add', err)
            commit('setFamiliesLoading', false)
            reject(new Error(error))
          })
      })
    },

    getFamiliesByFoundries({ commit, dispatch, state }, payload) {
      if (state.isFamiliesLoading) return Promise.resolve()

      commit('setFamiliesLoading', true)
      return dispatch('fetchFamiliesByFoundries', payload)
        .then(response => {
          commit('setFamilies', response.data.families)
          commit('setPagination', {
            ...state.pagination,
            ...response.data.meta
          })
          commit('setFamiliesLoading', false)
          return response
        })
        .catch(error => {
          const err = Errors.create(error)
          commit('errors/add', err)
          commit('setFamiliesLoading', false)
        })
    },

    loadMoreFamilies({ commit, dispatch, state }, payload) {
      if (state.isFamiliesLoading) return Promise.resolve()

      commit('setFamiliesLoading', true)
      return dispatch('fetchFamiliesByFoundries', payload)
        .then(response => {
          if (isEmpty(state.families)) {
            commit('setFamilies', response.data.families)
          } else {
            let families = [...state.families, ...response.data.families]
            families = uniqBy(families, 'md5')
            commit('setFamilies', families)
          }
          commit('setPagination', {
            ...state.pagination,
            ...response.data.meta
          })
          commit('setFamiliesLoading', false)
          return response
        })
        .catch(error => {
          commit('setFamiliesLoading', false)
          const err = Errors.create(error)
          commit('errors/add', err)
        })
    },

    updateBundleFontCollection({ commit, state }, payload) {
      return new Promise((resolve, reject) => {
        if (isEmpty(payload?.bundleId)) {
          return reject(new Error({
            'message': 'Invalid payload'
          }))
        }
        Api.Bundles.updateBundle(payload.bundleId, payload.bundleData)
          .then(response => {
            commit('UPDATE_BUNDLE_STATUS', response.data.bundle)
            resolve(response.data)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },
    putFontToBundle({ dispatch, commit, state }, payload) {
      if (isEmpty(payload.bundleId)) return
      if (isEmpty(payload.bundleData.collection)) return

      // save the original bundle info
      commit('UPDATE_BUNDLE_INFO', payload.bundleData)

      // map data to save on db
      const bundleData = cloneDeep(payload.bundleData)
      bundleData.collection = bundleData.collection.map((familyData, i) => {
        familyData = omit(familyData, ['isLoading', 'id', 'skus', 'oldSku'])
        familyData.rank = i + 1
        return familyData
      })

      return dispatch('updateBundleFontCollection', {
        bundleId: payload.bundleId,
        bundleData
      })
    },
    addFontToBundle({ state, commit, dispatch }, payload) {
      if (isEmpty(payload?.id)) return

      if (isEmpty(payload?.family) || isEmpty(payload?.skuId) && isEmpty(payload?.style)) return

      const newFamily = cloneDeep(payload.family)

      const skuInFamily = newFamily.skus.find(sku => sku.skuId === payload.skuId)

      // if sku is not present return
      if (isEmpty(skuInFamily)) return

      const fontStyle = skuInFamily.styles.find(style => style.styleId === payload.style.styleId)
      if (isEmpty(fontStyle)) return
      fontStyle.isFontStyleAdded = true

      skuInFamily.styles = [fontStyle]

      const skuPayload = {
        familyName: newFamily.familyName,
        familyId: newFamily.familyId,
        vendorID: newFamily.vendorID,
        vendorName: newFamily.vendorName,
        foundryId: newFamily.foundryId,
        styleId: fontStyle.styleId,
        ...skuInFamily
      }

      // check if sku existed in bundle collection
      const isFontStyleExistedInBundle =
        state.bundleInfo.collection?.find(family => family.styleId === fontStyle.styleId)

      // if already exists, return
      if (isFontStyleExistedInBundle?.styleId) return

      const collection = [...state.bundleInfo.collection, skuPayload]

      dispatch('putFontToBundle', {
        bundleId: payload.id,
        bundleData: {
          ...state.bundleInfo,
          collection
        }
      })
    },
    removeFoundryAndFont({ commit, state }, payload) {
      if (isEmpty(payload.foundryId) || isEmpty(payload.skuId)) return

      const bundleInfo = cloneDeep(state.bundleInfo)

      // remove selected foundry
      if (isEmpty(bundleInfo.foundries)) return

      bundleInfo.foundries = bundleInfo.foundries.filter(foundry => !isEqual(foundry.id, payload.foundryId))
      commit('UPDATE_BUNDLE_INFO', bundleInfo)
      commit('UPDATE_FOUNDRIES', bundleInfo.foundries)

      if (isEmpty(bundleInfo.collection)) return

      // remove all fonts of deselected foundry
      bundleInfo.collection = bundleInfo.collection.filter(family => !isEqual(family.foundryId, payload.foundryId) && !isEqual(family.skuId, payload.skuId))
      commit('UPDATE_BUNDLE_INFO', bundleInfo)

    },

    uploadBundleImage({ commit }, payload) {
      commit('incrementTotalImagesToUpload');
      const { bundleId, assets } = payload;
      let bundleImageRequest = {
        assets
      }

      return new Promise((resolve, reject) => {
        Api.Bundles.uploadBundleImage(bundleId, bundleImageRequest)
          .then(response => {
            commit('decrementTotalImagesToUpload');
            resolve(response.data)
          })
          .catch(error => {
            commit('decrementTotalImagesToUpload');
            assets?.[0].priority === 1 ? commit('setBundleMainImageScanStatus', 'unset') : commit('setBundleInspirationImageScanStatus', 'unset');
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    getBundleImageAvStatus({ commit }, bundleId) {
      return new Promise((resolve, reject) => {
        Api.Asset.getAssetsAvStatus(bundleId, 'bundle')
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            commit('setBundleMainImageScanStatus', "unset");
            commit('setBundleInspirationImageScanStatus', "unset");
            reject(err)
          })
      })
    },

    deleteBundleImage({ commit, state }, data) {
      return new Promise((resolve, reject) => {
        Api.Bundles.deleteBundleImage(data.payload.imageId, data.payload.bundleId)
          .then(response => {
            commit('updateAlreadyUploadedMd5', data.payload.md5)
            if (isEqual(data.priority, 1)) {
              commit('REMOVE_BUNDLE_IMAGE', data.payload.imageId)
            } else {
              commit('REMOVE_INSPIRATIONAL_IMAGE', data.payload.imageId)
            }
            resolve(response)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    updateStatus({ commit }, { id, mode }) {
      commit('updateLoader', true)
      return new Promise((resolve, reject) => {
        Api.Bundles.changeMode(id, { mode })
          .then(response => {
            commit('updateLoader', false)
            commit('setDeReleaseDate', response.data.deReleaseDate)
            commit('setBundleId', id)
            resolve(response)
          })
          .catch(error => {
            commit('updateLoader', false)
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    publishBundle({ commit, dispatch }, bundleId) {
      if (!bundleId) {
        commit('setConfirmPublishModalOpen', false)
        commit('setBundlePublishing', false)
        return Promise.reject(new Error('Bundle id is required'))
      }

      commit('setBundlePublishing', true)

      return dispatch('updateBundle', bundleId).then((res) => {
        commit('setConfirmPublishModalOpen', false)
        commit('setBundlePublishing', false)
        return res
      }).catch((error) => {
        commit('setConfirmPublishModalOpen', false)
        commit('setBundlePublishing', false)
        throw error
      })
    },
  },
  modules: {
    errors: VuexErrors
  }
}
